import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Plus, Trash2, Edit2, Loader2, Building2 } from 'lucide-react'
import { api } from '@/services/api'
import { toast } from 'sonner'

interface PropSize {
  id: number
  size: number
  is_active: boolean
}

interface PropProvider {
  id: number
  name: string
  code: string
  is_active: boolean
  created_at: string
  updated_at: string
  sizes: PropSize[]
}

export const PropFirms = () => {
  const [providers, setProviders] = useState<PropProvider[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddProvider, setShowAddProvider] = useState(false)
  const [showAddSize, setShowAddSize] = useState<number | null>(null)
  const [editingProvider, setEditingProvider] = useState<PropProvider | null>(null)
  const [editingSize, setEditingSize] = useState<{ providerId: number; size: PropSize } | null>(null)

  const [newProviderName, setNewProviderName] = useState('')
  const [newProviderCode, setNewProviderCode] = useState('')
  const [newSize, setNewSize] = useState('')

  useEffect(() => {
    loadProviders()
  }, [])

  const loadProviders = async () => {
    try {
      const data = await api.get<PropProvider[]>('/admin/prop-providers')
      setProviders(data)
    } catch (error) {
      toast.error('Failed to load prop providers')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateProvider = async () => {
    if (!newProviderName || !newProviderCode) {
      toast.error('Provider name and code are required')
      return
    }

    try {
      await api.post('/admin/prop-providers', {
        name: newProviderName,
        code: newProviderCode
      })
      toast.success('Provider created successfully')
      setNewProviderName('')
      setNewProviderCode('')
      setShowAddProvider(false)
      loadProviders()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to create provider')
    }
  }

  const handleUpdateProvider = async () => {
    if (!editingProvider) return

    try {
      await api.put(`/admin/prop-providers/${editingProvider.id}`, {
        name: editingProvider.name,
        code: editingProvider.code,
        is_active: editingProvider.is_active
      })
      toast.success('Provider updated successfully')
      setEditingProvider(null)
      loadProviders()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update provider')
    }
  }

  const handleDeleteProvider = async (providerId: number) => {
    if (!confirm('Are you sure? This will delete the provider and all associated sizes.')) return

    try {
      await api.delete(`/admin/prop-providers/${providerId}`)
      toast.success('Provider deleted successfully')
      loadProviders()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to delete provider')
    }
  }

  const handleCreateSize = async (providerId: number) => {
    if (!newSize) {
      toast.error('Size is required')
      return
    }

    const sizeValue = parseInt(newSize)
    if (isNaN(sizeValue) || sizeValue <= 0) {
      toast.error('Size must be a positive number')
      return
    }

    try {
      await api.post(`/admin/prop-providers/${providerId}/sizes`, {
        size: sizeValue
      })
      toast.success('Size added successfully')
      setNewSize('')
      setShowAddSize(null)
      loadProviders()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to add size')
    }
  }

  const handleUpdateSize = async () => {
    if (!editingSize) return

    try {
      await api.put(`/admin/prop-sizes/${editingSize.size.id}`, {
        size: editingSize.size.size,
        is_active: editingSize.size.is_active
      })
      toast.success('Size updated successfully')
      setEditingSize(null)
      loadProviders()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update size')
    }
  }

  const handleDeleteSize = async (sizeId: number) => {
    if (!confirm('Are you sure you want to delete this size?')) return

    try {
      await api.delete(`/admin/prop-sizes/${sizeId}`)
      toast.success('Size deleted successfully')
      loadProviders()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to delete size')
    }
  }

  const formatSize = (size: number) => {
    if (size >= 1000) {
      return `$${(size / 1000).toFixed(0)}k`
    }
    return `$${size}`
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Prop Firms Management</h2>
          <p className="text-sm text-muted-foreground">Manage prop providers and their account sizes</p>
        </div>
        <Button onClick={() => setShowAddProvider(true)} disabled={showAddProvider}>
          <Plus className="w-4 h-4 mr-2" />
          Add Provider
        </Button>
      </div>

      {showAddProvider && (
        <Card className="border-primary">
          <CardHeader>
            <CardTitle>New Prop Provider</CardTitle>
            <CardDescription>Add a new prop trading firm</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="provider-name">Provider Name</Label>
                <Input
                  id="provider-name"
                  placeholder="e.g., FTMO, FundedNext"
                  value={newProviderName}
                  onChange={(e) => setNewProviderName(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="provider-code">Code (3 chars)</Label>
                <Input
                  id="provider-code"
                  placeholder="e.g., FTM, FND"
                  maxLength={3}
                  value={newProviderCode}
                  onChange={(e) => setNewProviderCode(e.target.value.toUpperCase())}
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => {
                setShowAddProvider(false)
                setNewProviderName('')
                setNewProviderCode('')
              }}>
                Cancel
              </Button>
              <Button onClick={handleCreateProvider}>Create Provider</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4">
        {providers.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Building2 className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-lg font-medium">No prop providers configured</p>
              <p className="text-sm text-muted-foreground">Add your first provider to get started</p>
            </CardContent>
          </Card>
        ) : (
          providers.map((provider) => (
            <Card key={provider.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <Building2 className="w-6 h-6 text-primary" />
                    <div>
                      {editingProvider?.id === provider.id ? (
                        <div className="space-y-2">
                          <Input
                            value={editingProvider.name}
                            onChange={(e) => setEditingProvider({ ...editingProvider, name: e.target.value })}
                            className="font-semibold"
                          />
                          <Input
                            value={editingProvider.code}
                            onChange={(e) => setEditingProvider({ ...editingProvider, code: e.target.value.toUpperCase() })}
                            maxLength={3}
                          />
                        </div>
                      ) : (
                        <>
                          <CardTitle>{provider.name}</CardTitle>
                          <CardDescription>Code: {provider.code}</CardDescription>
                        </>
                      )}
                    </div>
                    <Badge variant={provider.is_active ? 'default' : 'secondary'}>
                      {provider.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <div className="flex items-center space-x-2">
                    {editingProvider?.id === provider.id ? (
                      <>
                        <Button size="sm" variant="outline" onClick={() => setEditingProvider(null)}>
                          Cancel
                        </Button>
                        <Button size="sm" onClick={handleUpdateProvider}>
                          Save
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button size="sm" variant="outline" onClick={() => setEditingProvider(provider)}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleDeleteProvider(provider.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-sm font-medium">Account Sizes</Label>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowAddSize(provider.id)}
                      disabled={showAddSize === provider.id}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add Size
                    </Button>
                  </div>

                  {showAddSize === provider.id && (
                    <div className="flex items-end space-x-2 mb-3 p-3 bg-slate-800 rounded-lg">
                      <div className="flex-1">
                        <Label htmlFor="new-size" className="text-xs">Size (USD)</Label>
                        <Input
                          id="new-size"
                          type="number"
                          placeholder="e.g., 50000, 100000, 200000"
                          value={newSize}
                          onChange={(e) => setNewSize(e.target.value)}
                        />
                      </div>
                      <Button size="sm" onClick={() => handleCreateSize(provider.id)}>
                        Add
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => {
                        setShowAddSize(null)
                        setNewSize('')
                      }}>
                        Cancel
                      </Button>
                    </div>
                  )}

                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                    {provider.sizes.length === 0 ? (
                      <p className="text-sm text-muted-foreground col-span-full">No sizes configured</p>
                    ) : (
                      provider.sizes.map((size) => (
                        <div
                          key={size.id}
                          className="flex items-center justify-between p-2 bg-slate-800 rounded-lg border border-slate-700"
                        >
                          {editingSize?.size.id === size.id ? (
                            <Input
                              type="number"
                              value={editingSize.size.size}
                              onChange={(e) => setEditingSize({
                                ...editingSize,
                                size: { ...editingSize.size, size: parseInt(e.target.value) }
                              })}
                              className="h-7 text-sm"
                            />
                          ) : (
                            <span className="text-sm font-medium">{formatSize(size.size)}</span>
                          )}
                          <div className="flex items-center space-x-1">
                            {editingSize?.size.id === size.id ? (
                              <>
                                <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={handleUpdateSize}>
                                  ✓
                                </Button>
                                <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setEditingSize(null)}>
                                  ✕
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 w-6 p-0"
                                  onClick={() => setEditingSize({ providerId: provider.id, size })}
                                >
                                  <Edit2 className="w-3 h-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 w-6 p-0 text-red-500 hover:text-red-600"
                                  onClick={() => handleDeleteSize(size.id)}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  )
}
